import { DocumentPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { apiService } from '../../services/api';
import { showIntegrationErrorToast } from '../../utils/integrationErrorToast';
import { Vehicle } from '../../types';
import RichTextEditor from '../ui/RichTextEditor';
import BHPHSettingsSection from './BHPHSettingsSection';
import {
  buildDescriptionHtml,
  VehicleDescriptionStructured,
  VehicleFormSnapshot,
} from './buildDescriptionHtml';
import QRCodeUploader from './QRCodeUploader';
import VehicleImageUpload from './VehicleImageUpload';

// Vehicle form schema
const vehicleSchema = z.object({
  vin: z.string().min(17, 'VIN must be 17 characters').max(17, 'VIN must be 17 characters'),
  stock_number: z.string().optional(),
  year: z
    .number()
    .min(1900)
    .max(new Date().getFullYear() + 1),
  make: z.string().min(1, 'Make is required'),
  model: z.string().min(1, 'Model is required'),
  trim: z.string().optional(),
  mileage: z.number().min(0),
  exterior_color: z.string().optional(),
  interior_color: z.string().optional(),
  body_style: z.string().optional(),
  drivetrain: z.string().optional(),
  engine: z.string().optional(),
  transmission: z.string().optional(),
  fuel_type: z.string().optional(),
  asking_price: z.number().min(0).optional(),
  purchase_price: z.number().min(0).optional(),
  market_value: z.number().min(0).optional(),
  status: z.enum(['available', 'pending', 'sold', 'wholesale', 'repo', 'service']),
  location: z.string().optional(),
  description: z.string().optional(),
  features: z.array(z.string()).optional(),
  bhph_eligible: z.boolean().optional().default(false),
  bhph_min_down_type: z.string().optional().nullable(),
  bhph_min_down_value: z.preprocess(
    (v) => (v === '' || v === undefined ? null : Number(v)),
    z.number().nullable().optional()
  ),
  bhph_payment_amount: z.preprocess(
    (v) => (v === '' || v === undefined ? null : Number(v)),
    z.number().nullable().optional()
  ),
  bhph_payment_frequency: z.string().optional().nullable(),
  bhph_term_months: z.preprocess(
    (v) => (v === '' || v === undefined ? null : Number(v)),
    z.number().int().nullable().optional()
  ),
});

type VehicleFormData = z.infer<typeof vehicleSchema>;

interface VehicleImage {
  id: string;
  file_name: string;
  file_path: string;
  is_primary: boolean;
  display_order: number;
  isExternalUrl?: boolean;
}

interface VehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (vehicleData: VehicleFormData) => Promise<Vehicle | void>;
  vehicle?: Vehicle | null;
  loading?: boolean;
}

const VehicleModal: React.FC<VehicleModalProps> = ({
  isOpen,
  onClose,
  onSave,
  vehicle,
  loading = false,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<
    'basic' | 'details' | 'pricing' | 'description' | 'images'
  >('basic');
  const [featuresInput, setFeaturesInput] = useState<string>('');
  const [images, setImages] = useState<VehicleImage[]>([]);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showAdvancedPricing, setShowAdvancedPricing] = useState(false);
  const [vinDecoding, setVinDecoding] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [saveMessage, setSaveMessage] = useState<string>('');
  const [warrantyOptions, setWarrantyOptions] = useState<Array<{ label: string; content: string }>>(
    []
  );
  const [selectedWarranty, setSelectedWarranty] = useState<string>('');
  const [descriptionAiGenerated, setDescriptionAiGenerated] = useState(false);
  const [descriptionUserModified, setDescriptionUserModified] = useState(false);
  const [lastAiDescriptionText, setLastAiDescriptionText] = useState('');
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isRefactoringDescription, setIsRefactoringDescription] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      status: 'available',
    },
  });

  // Watch the description field to ensure reactivity
  const watchedDescription = watch('description');

  // Fetch warranty options once on mount
  useEffect(() => {
    const fetchWarrantySettings = async () => {
      try {
        const response = await apiService.get<{
          warranty_options?: Array<{ label: string; content: string }>;
        }>('/settings/inventory');
        setWarrantyOptions(response?.warranty_options ?? []);
      } catch (error) {
        console.error('Error fetching warranty settings:', error);
      }
    };
    fetchWarrantySettings();
  }, []);

  useEffect(() => {
    if (vehicle) {
      // Populate form with vehicle data
      Object.keys(vehicle).forEach((key) => {
        const value = vehicle[key as keyof Vehicle];
        if (key === 'features' && Array.isArray(value)) {
          setFeaturesInput(value.join(', '));
          setValue('features', value);
        } else if (value !== undefined && value !== null) {
          setValue(key as keyof VehicleFormData, value as any);
        }
      });

      // Fetch existing images
      fetchVehicleImages(vehicle.id);
      setShowImageUpload(true);
    } else {
      // Reset form for new vehicle
      reset({
        status: 'available',
      });
      setFeaturesInput('');
      setImages([]);
      setShowImageUpload(true); // Show image upload for new vehicles
      setActiveTab('basic'); // Start with basic info for new vehicles
    }
  }, [vehicle, setValue, reset, isOpen]);

  const fetchVehicleImages = async (vehicleId: string) => {
    try {
      const vehicleImages = await apiService.get<VehicleImage[]>(
        `/vehicles/${vehicleId}/vehicle-images`
      );
      if (vehicleImages && vehicleImages.length > 0) {
        setImages(vehicleImages);
        return;
      }

      // Fallback: CFS-imported images live in vehicles.images JSON (external URLs).
      // Display read-only — VehicleImageUpload disables mutation when isExternalUrl is true.
      const vehicleData = await apiService.get<{ images?: string[] }>(`/vehicles/${vehicleId}`);
      if (vehicleData?.images && vehicleData.images.length > 0) {
        const externalImages: VehicleImage[] = vehicleData.images.map((url, index) => ({
          id: `external-${index}`,
          file_name: url.split('/').pop() || `image-${index}`,
          file_path: url,
          is_primary: index === 0,
          display_order: index,
          isExternalUrl: true,
        }));
        setImages(externalImages);
        return;
      }

      setImages([]);
    } catch (error) {
      console.error('Error fetching vehicle images:', error);
      setImages([]);
    }
  };

  // Handler functions
  const handleCreateDeal = () => {
    if (!vehicle) return;
    navigate('/calculator', {
      state: {
        selectedVehicle: {
          id: vehicle.id,
          vin: vehicle.vin,
          year: vehicle.year,
          make: vehicle.make,
          model: vehicle.model,
          askingPrice: vehicle.asking_price,
        },
      },
    });
    onClose();
  };

  const handleVINDecode = async (vin: string) => {
    if (!vin || vin.length !== 17) {
      toast.error('Please enter a valid 17-character VIN');
      return;
    }

    setVinDecoding(true);
    try {
      // Call real VIN decode API with form data
      const formData = new FormData();
      formData.append('vin', vin);

      const response: any = await apiService.post('/vehicles/decode-vin', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.success && response.decoded_data) {
        const decodedData = response.decoded_data;

        // Populate form fields with decoded data
        if (decodedData.year) setValue('year', decodedData.year);
        if (decodedData.make) setValue('make', decodedData.make);
        if (decodedData.model) setValue('model', decodedData.model);
        if (decodedData.trim) setValue('trim', decodedData.trim);
        if (decodedData.body_style) setValue('body_style', decodedData.body_style);
        if (decodedData.drivetrain) {
          const drivetrain = decodedData.drivetrain.toLowerCase();
          let mappedDrivetrain = '';
          if (drivetrain.includes('fwd') || drivetrain.includes('front')) {
            mappedDrivetrain = 'FWD';
          } else if (drivetrain.includes('rwd') || drivetrain.includes('rear')) {
            mappedDrivetrain = 'RWD';
          } else if (drivetrain.includes('awd') || drivetrain.includes('all')) {
            mappedDrivetrain = 'AWD';
          } else if (drivetrain.includes('4wd') || drivetrain.includes('four')) {
            mappedDrivetrain = '4WD';
          } else if (drivetrain.includes('4x4')) {
            mappedDrivetrain = '4x4';
          }
          if (mappedDrivetrain) setValue('drivetrain', mappedDrivetrain);
        }
        if (decodedData.engine) setValue('engine', decodedData.engine);

        // Map fuel type to dropdown values
        if (decodedData.fuel_type) {
          const fuelType = decodedData.fuel_type.toLowerCase();
          let mappedFuelType = '';
          if (fuelType.includes('gasoline') || fuelType.includes('gas')) {
            mappedFuelType = 'gasoline';
          } else if (fuelType.includes('diesel')) {
            mappedFuelType = 'diesel';
          } else if (fuelType.includes('hybrid') && fuelType.includes('plug')) {
            mappedFuelType = 'flex_fuel';
          } else if (fuelType.includes('hybrid')) {
            mappedFuelType = 'hybrid';
          } else if (fuelType.includes('electric')) {
            mappedFuelType = 'electric';
          }
          if (mappedFuelType) setValue('fuel_type', mappedFuelType);
        }

        // Map transmission to dropdown values
        if (decodedData.transmission) {
          const transmission = decodedData.transmission.toLowerCase();
          let mappedTransmission = '';
          if (transmission.includes('manual')) {
            mappedTransmission = 'Manual';
          } else if (transmission.includes('cvt') || transmission.includes('continuously')) {
            mappedTransmission = 'CVT';
          } else if (transmission.includes('automatic') || transmission.includes('auto')) {
            mappedTransmission = 'Automatic';
          }
          if (mappedTransmission) setValue('transmission', mappedTransmission);
        }

        toast.success('VIN decoded successfully!');
      } else {
        toast.error('Unable to decode VIN. Please check the VIN and try again.');
      }
    } catch (error: any) {
      console.error('VIN decode error:', error);

      // Handle different types of error responses
      let errorMessage = 'Failed to decode VIN. Please try again.';

      if (error?.response?.data) {
        const data = error.response.data;

        // Handle FastAPI validation errors
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail
            .map((err: any) => (typeof err === 'object' && err.msg ? err.msg : 'Validation error'))
            .join(', ');
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.message) {
          errorMessage = data.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setVinDecoding(false);
    }
  };

  const handleAIDescription = async () => {
    if (isGeneratingDescription) return;
    try {
      if (!vehicle?.id) {
        toast.error('Save the vehicle first, then use AI Generate');
        return;
      }

      const values = getValues();
      const {
        year,
        make,
        model,
        trim,
        engine,
        transmission,
        fuel_type,
        drivetrain,
        exterior_color,
        interior_color,
        body_style,
        asking_price,
        bhph_eligible,
        bhph_min_down_type,
        bhph_min_down_value,
        bhph_payment_amount,
        bhph_payment_frequency,
        bhph_term_months,
      } = values;

      if (!year || !make || !model) {
        toast.error('Please fill in Year, Make, and Model first');
        return;
      }

      const chosenOption = warrantyOptions.find((o) => o.label === selectedWarranty);
      if (!chosenOption) {
        toast.error('Please select a Warranty Disclaimer before generating the description');
        return;
      }

      // Use any existing description (CFS-imported, manually edited, or prior AI run)
      // as a baseline so the LLM enhances rather than regenerates from scratch.
      const existingDescription = (getValues('description') || '').trim();
      const stripHtml = (h: string) =>
        h
          .replace(/<[^>]+>/g, ' ')
          .replace(/&nbsp;/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      const baselineText = existingDescription ? stripHtml(existingDescription) : '';

      setIsGeneratingDescription(true);
      const structured = await apiService.post<VehicleDescriptionStructured>(
        '/ai/generate-description',
        {
          vehicle_id: vehicle.id,
          style: 'professional',
          length: 'medium',
          current_description: baselineText || undefined,
        }
      );

      const snapshot: VehicleFormSnapshot = {
        year,
        make,
        model,
        trim,
        engine,
        transmission,
        fuel_type,
        drivetrain,
        exterior_color,
        interior_color,
        body_style,
        asking_price,
        bhph_eligible,
        bhph_min_down_type,
        bhph_min_down_value,
        bhph_payment_amount,
        bhph_payment_frequency,
        bhph_term_months,
      };

      const html = buildDescriptionHtml(structured, snapshot, chosenOption.content);

      setValue('description', html, { shouldDirty: true, shouldTouch: true });
      setLastAiDescriptionText(stripHtml(html));
      setDescriptionAiGenerated(true);
      setDescriptionUserModified(false);

      if (structured.fallback) {
        toast(
          (t) => (
            <div className="flex flex-col gap-2">
              <span>
                AI not configured — description generated locally. Add an API key for richer
                polishing.
              </span>
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(t.id);
                  navigate('/settings?category=ai');
                }}
                className="text-left text-sm font-semibold text-blue-600 underline hover:text-blue-800"
              >
                Set up AI provider →
              </button>
            </div>
          ),
          { icon: 'ℹ️', duration: 8000 }
        );
      } else {
        toast.success('✨ Description generated by AI!');
      }
    } catch (error) {
      console.error('AI description error:', error);
      showIntegrationErrorToast(error, {
        navigate,
        setupPath: '/settings?category=ai',
        setupLabel: 'Set up AI provider',
        fallbackMessage: 'Failed to generate description',
      });
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleRefactorDescription = async () => {
    if (isRefactoringDescription) return;
    try {
      if (!vehicle?.id) {
        toast.error('Save the vehicle first to use Refactor with AI');
        return;
      }

      const currentDesc = getValues('description') || '';
      const chosenOption = warrantyOptions.find((o) => o.label === selectedWarranty);
      if (!chosenOption) {
        toast.error('Please select a Warranty Disclaimer before refactoring');
        return;
      }

      const stripHtml = (html: string) =>
        html
          .replace(/<[^>]+>/g, ' ')
          .replace(/&nbsp;/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();

      const findInsertedRuns = (orig: string, curr: string): string[] => {
        const tokenize = (t: string) => t.split(/\s+/).filter((w) => w.length > 0);
        const o = tokenize(orig);
        const c = tokenize(curr);
        const norm = (w: string) => w.toLowerCase().replace(/[^\w]/g, '');
        const m = o.length;
        const n = c.length;
        if (m === 0 || n === 0) return [];
        const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
        for (let i = 1; i <= m; i++) {
          for (let j = 1; j <= n; j++) {
            dp[i][j] =
              norm(o[i - 1]) === norm(c[j - 1])
                ? dp[i - 1][j - 1] + 1
                : Math.max(dp[i - 1][j], dp[i][j - 1]);
          }
        }
        const runs: string[] = [];
        let run: string[] = [];
        let i = m,
          j = n;
        while (j > 0) {
          if (i > 0 && norm(o[i - 1]) === norm(c[j - 1])) {
            if (run.length > 0) {
              runs.unshift(run.reverse().join(' '));
              run = [];
            }
            i--;
            j--;
          } else if (i === 0 || dp[i][j - 1] >= dp[i - 1][j]) {
            run.push(c[j - 1]);
            j--;
          } else {
            i--;
          }
        }
        if (run.length > 0) runs.unshift(run.reverse().join(' '));
        return runs;
      };

      const currentText = stripHtml(currentDesc);
      const insertedRuns = lastAiDescriptionText
        ? findInsertedRuns(lastAiDescriptionText, currentText)
        : [];
      const detectedAdditions = insertedRuns
        .flatMap((run) => run.split(/[,.\n|]+/).map((s) => s.trim()))
        .filter((s) => {
          const wc = s.split(/\s+/).filter((w) => w.length > 1).length;
          return s.length >= 3 && s.length <= 80 && wc >= 1 && wc <= 6;
        })
        .slice(0, 10);

      const values = getValues();
      const {
        year,
        make,
        model,
        trim,
        engine,
        transmission,
        fuel_type,
        drivetrain,
        exterior_color,
        interior_color,
        body_style,
        asking_price,
        bhph_eligible,
        bhph_min_down_type,
        bhph_min_down_value,
        bhph_payment_amount,
        bhph_payment_frequency,
        bhph_term_months,
      } = values;

      setIsRefactoringDescription(true);
      const structured = await apiService.post<VehicleDescriptionStructured>(
        '/ai/generate-description',
        {
          vehicle_id: vehicle.id,
          style: 'professional',
          length: 'medium',
          current_description: currentText,
          detected_additions: detectedAdditions.length > 0 ? detectedAdditions : undefined,
        }
      );

      const snapshot: VehicleFormSnapshot = {
        year,
        make,
        model,
        trim,
        engine,
        transmission,
        fuel_type,
        drivetrain,
        exterior_color,
        interior_color,
        body_style,
        asking_price,
        bhph_eligible,
        bhph_min_down_type,
        bhph_min_down_value,
        bhph_payment_amount,
        bhph_payment_frequency,
        bhph_term_months,
      };

      const html = buildDescriptionHtml(structured, snapshot, chosenOption.content);

      setValue('description', html, { shouldDirty: true });
      setLastAiDescriptionText(stripHtml(html));
      setDescriptionUserModified(false);

      if (structured.fallback) {
        toast(
          'AI not configured — refactored locally. Add an API key in Settings → Integrations for richer polishing.',
          { icon: 'ℹ️' }
        );
      } else {
        toast.success('✨ Description polished by AI');
      }
    } catch (error) {
      toast.error('Failed to refactor description');
      console.error('Refactor error:', error);
    } finally {
      setIsRefactoringDescription(false);
    }
  };

  // Retained for future re-enabling — see "Suggest Features hidden" comment in the Description tab.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSuggestFeatures = async () => {
    try {
      const values = getValues();
      const { make, body_style, fuel_type, year } = values;

      // Base features for all vehicles
      let suggestedFeatures = [
        'Air Conditioning',
        'Power Windows',
        'Power Locks',
        'Cruise Control',
        'Keyless Entry',
      ];

      // Add features based on vehicle type
      if (
        body_style?.toLowerCase().includes('suv') ||
        body_style?.toLowerCase().includes('crossover')
      ) {
        suggestedFeatures.push(
          'Third Row Seating',
          'Roof Rails',
          'All-Wheel Drive',
          'Towing Package'
        );
      } else if (body_style?.toLowerCase().includes('truck')) {
        suggestedFeatures.push('Towing Package', 'Bed Liner', 'Four-Wheel Drive', 'Trailer Hitch');
      } else if (
        body_style?.toLowerCase().includes('sedan') ||
        body_style?.toLowerCase().includes('coupe')
      ) {
        suggestedFeatures.push('Leather Seats', 'Sunroof', 'Premium Audio', 'Navigation System');
      }

      // Add features based on fuel type
      if (
        fuel_type?.toLowerCase().includes('hybrid') ||
        fuel_type?.toLowerCase().includes('electric')
      ) {
        suggestedFeatures.push('Regenerative Braking', 'Eco Mode', 'Energy Monitor');
      }

      // Add modern features for newer vehicles
      if (year && year >= 2018) {
        suggestedFeatures.push(
          'Bluetooth Connectivity',
          'Backup Camera',
          'Apple CarPlay/Android Auto',
          'USB Ports',
          'Wireless Charging'
        );
      }

      // Add luxury features for premium brands
      if (
        make?.toLowerCase().includes('mercedes') ||
        make?.toLowerCase().includes('bmw') ||
        make?.toLowerCase().includes('audi') ||
        make?.toLowerCase().includes('lexus') ||
        make?.toLowerCase().includes('cadillac')
      ) {
        suggestedFeatures.push(
          'Heated Seats',
          'Premium Sound System',
          'Adaptive Cruise Control',
          'Lane Keep Assist'
        );
      }

      // Remove duplicates and limit to 12 features
      const uniqueFeatures = Array.from(new Set(suggestedFeatures)).slice(0, 12);

      // Update the input field
      setFeaturesInput(uniqueFeatures.join(', '));

      // Also update the form state
      setValue('features', uniqueFeatures);

      toast.success(
        `🎯 Suggested ${uniqueFeatures.length} features based on vehicle specifications!`
      );
    } catch (error) {
      toast.error('Failed to suggest features');
      console.error('Features suggestion error:', error);
    }
  };

  const onSubmit = async (data: VehicleFormData) => {
    try {
      setSaveStatus('saving');
      setSaveMessage('Saving vehicle...');

      // Parse features from comma-separated string
      if (featuresInput.trim()) {
        data.features = featuresInput
          .split(',')
          .map((f) => f.trim())
          .filter((f) => f.length > 0);
      } else {
        data.features = [];
      }

      // Ensure numeric fields are properly typed
      if (data.year) data.year = Number(data.year);
      if (data.mileage) data.mileage = Number(data.mileage);
      if (data.asking_price) data.asking_price = Number(data.asking_price);
      if (data.purchase_price) data.purchase_price = Number(data.purchase_price);
      if (data.market_value) data.market_value = Number(data.market_value);

      const savedVehicle = await onSave(data);

      setSaveStatus('success');
      setSaveMessage(vehicle ? 'Vehicle updated successfully!' : 'Vehicle created successfully!');

      // Show success message for a moment
      setTimeout(() => {
        setSaveStatus('idle');
        setSaveMessage('');
      }, 2000);

      // If it was a new vehicle, enable image upload
      if (!vehicle && savedVehicle) {
        setShowImageUpload(true);
        setActiveTab('images');
        // Don't close modal yet, let user add images
        return;
      }

      reset();
      setFeaturesInput('');
      setImages([]);
      onClose();
    } catch (error: any) {
      console.error('Error saving vehicle:', error);
      setSaveStatus('error');

      // Extract meaningful error message
      let errorMessage = 'Failed to save vehicle. Please try again.';

      if (error?.response?.data) {
        const data = error.response.data;

        // Handle custom error middleware format: {error: {details: {validation_errors: [...]}}}
        if (data.error?.details?.validation_errors) {
          errorMessage = data.error.details.validation_errors
            .map((err: any) => `${err.field}: ${err.message}`)
            .join('; ');
        } else if (data.error?.message) {
          errorMessage = data.error.message;
          // Handle FastAPI default validation errors: {detail: [...]}
        } else if (Array.isArray(data.detail)) {
          errorMessage = data.detail
            .map((err: any) => (typeof err === 'object' && err.msg ? err.msg : 'Validation error'))
            .join(', ');
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.message) {
          errorMessage = data.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setSaveMessage(errorMessage);

      // Reset error status after 5 seconds
      setTimeout(() => {
        setSaveStatus('idle');
        setSaveMessage('');
      }, 5000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        />

        <div
          className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Loading Overlay */}
          {saveStatus === 'saving' && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">{saveMessage}</p>
              </div>
            </div>
          )}

          <div className="absolute right-0 top-0 pr-4 pt-4">
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 disabled:opacity-50"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              disabled={saveStatus === 'saving'}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <div className="flex justify-between items-center mb-6 pr-12">
                <h3 className="text-lg font-semibold leading-6 text-gray-900">
                  {vehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
                </h3>
                {vehicle && (
                  <button
                    type="button"
                    className="btn-secondary flex items-center text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateDeal();
                    }}
                  >
                    <DocumentPlusIcon className="h-4 w-4 mr-1" />
                    Create Deal
                  </button>
                )}
              </div>

              {/* Tab Navigation */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  {[
                    { id: 'basic', label: 'Basic Info', icon: '🚗' },
                    { id: 'details', label: 'Details', icon: '🔧' },
                    { id: 'pricing', label: 'Pricing', icon: '💰' },
                    { id: 'description', label: 'Description', icon: '📝' },
                    { id: 'images', label: 'Images', icon: '📸' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="mr-2">{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <form
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-6"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Basic Info Tab */}
                {activeTab === 'basic' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="form-label">VIN *</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            className={`form-input flex-1 ${errors.vin ? 'border-red-500' : ''} ${saveStatus === 'saving' ? 'opacity-50 cursor-not-allowed' : ''}`}
                            {...register('vin')}
                            placeholder="17-character VIN"
                            maxLength={17}
                            disabled={saveStatus === 'saving'}
                            onChange={(e) => {
                              register('vin').onChange(e);
                              if (e.target.value.length === 17) {
                                handleVINDecode(e.target.value);
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleVINDecode(getValues('vin'))}
                            disabled={vinDecoding || saveStatus === 'saving'}
                            className="btn-secondary whitespace-nowrap disabled:opacity-50"
                          >
                            {vinDecoding ? 'Decoding...' : 'Decode VIN'}
                          </button>
                        </div>
                        {errors.vin && (
                          <p className="text-red-500 text-sm mt-1">
                            {typeof errors.vin === 'object' && errors.vin.message
                              ? errors.vin.message
                              : 'Invalid VIN'}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="form-label">Stock Number</label>
                        <input
                          type="text"
                          className="form-input"
                          {...register('stock_number')}
                          placeholder="Auto-generated if empty"
                        />
                      </div>

                      <div>
                        <label className="form-label">Year *</label>
                        <input
                          type="number"
                          className={`form-input ${errors.year ? 'border-red-500' : ''} ${saveStatus === 'saving' ? 'opacity-50 cursor-not-allowed' : ''}`}
                          {...register('year', { valueAsNumber: true })}
                          placeholder="2020"
                          min="1900"
                          max={new Date().getFullYear() + 1}
                          disabled={saveStatus === 'saving'}
                        />
                        {errors.year && (
                          <p className="text-red-500 text-sm mt-1">
                            {typeof errors.year === 'object' && errors.year.message
                              ? errors.year.message
                              : 'Invalid year'}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="form-label">Make *</label>
                        <input
                          type="text"
                          className={`form-input ${errors.make ? 'border-red-500' : ''} ${saveStatus === 'saving' ? 'opacity-50 cursor-not-allowed' : ''}`}
                          {...register('make')}
                          placeholder="Ford"
                          disabled={saveStatus === 'saving'}
                        />
                        {errors.make && (
                          <p className="text-red-500 text-sm mt-1">
                            {typeof errors.make === 'object' && errors.make.message
                              ? errors.make.message
                              : 'Make is required'}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="form-label">Model *</label>
                        <input
                          type="text"
                          className={`form-input ${errors.model ? 'border-red-500' : ''} ${saveStatus === 'saving' ? 'opacity-50 cursor-not-allowed' : ''}`}
                          {...register('model')}
                          placeholder="F-150"
                          disabled={saveStatus === 'saving'}
                        />
                        {errors.model && (
                          <p className="text-red-500 text-sm mt-1">
                            {typeof errors.model === 'object' && errors.model.message
                              ? errors.model.message
                              : 'Model is required'}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="form-label">Trim</label>
                        <input
                          type="text"
                          className="form-input"
                          {...register('trim')}
                          placeholder="XLT"
                        />
                      </div>

                      <div>
                        <label className="form-label">Mileage *</label>
                        <input
                          type="number"
                          className={`form-input ${errors.mileage ? 'border-red-500' : ''}`}
                          {...register('mileage', { valueAsNumber: true })}
                          placeholder="50000"
                          min="0"
                        />
                        {errors.mileage && (
                          <p className="text-red-500 text-sm mt-1">
                            {typeof errors.mileage === 'object' && errors.mileage.message
                              ? errors.mileage.message
                              : 'Invalid mileage'}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="form-label">Status *</label>
                        <select
                          className={`form-input ${errors.status ? 'border-red-500' : ''}`}
                          {...register('status')}
                        >
                          <option value="available">Available</option>
                          <option value="sold">Sold</option>
                          <option value="pending">Pending</option>
                          <option value="wholesale">Wholesale</option>
                          <option value="repo">Repo</option>
                          <option value="service">Service</option>
                        </select>
                        {errors.status && (
                          <p className="text-red-500 text-sm mt-1">
                            {typeof errors.status === 'object' && errors.status.message
                              ? errors.status.message
                              : 'Status is required'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Details Tab */}
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Exterior Color</label>
                        <input
                          type="text"
                          className="form-input"
                          {...register('exterior_color')}
                          placeholder="Red"
                        />
                      </div>
                      <div>
                        <label className="form-label">Interior Color</label>
                        <input
                          type="text"
                          className="form-input"
                          {...register('interior_color')}
                          placeholder="Black"
                        />
                      </div>
                      <div>
                        <label className="form-label">Drivetrain</label>
                        <select className="form-input" {...register('drivetrain')}>
                          <option value="">Select drivetrain</option>
                          <option value="FWD">FWD</option>
                          <option value="RWD">RWD</option>
                          <option value="AWD">AWD</option>
                          <option value="4WD">4WD</option>
                          <option value="4x4">4x4</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Location</label>
                        <select className="form-input" {...register('location')}>
                          <option value="">Select location</option>
                          <option value="lot">Lot</option>
                          <option value="service">Service</option>
                          <option value="offsite">Offsite</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Engine</label>
                        <input
                          type="text"
                          className="form-input"
                          {...register('engine')}
                          placeholder="3.5L V6"
                        />
                      </div>
                      <div>
                        <label className="form-label">Transmission</label>
                        <select className="form-input" {...register('transmission')}>
                          <option value="">Select transmission</option>
                          <option value="Manual">Manual</option>
                          <option value="Automatic">Automatic</option>
                          <option value="CVT">CVT</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Fuel Type</label>
                        <select className="form-input" {...register('fuel_type')}>
                          <option value="">Select fuel type</option>
                          <option value="gasoline">Gasoline</option>
                          <option value="diesel">Diesel</option>
                          <option value="hybrid">Hybrid</option>
                          <option value="electric">Electric</option>
                          <option value="flex_fuel">Flex Fuel</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pricing Tab */}
                {activeTab === 'pricing' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="form-label">Asking Price *</label>
                        <input
                          type="number"
                          className="form-input"
                          {...register('asking_price', { valueAsNumber: true })}
                          placeholder="25000"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-2 border-t">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Advanced Pricing</h4>
                        <p className="text-xs text-gray-500">
                          Optional purchase and market value fields
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAdvancedPricing(!showAdvancedPricing)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          showAdvancedPricing ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            showAdvancedPricing ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {showAdvancedPricing && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="form-label">Purchase Price</label>
                          <input
                            type="number"
                            className="form-input"
                            {...register('purchase_price', { valueAsNumber: true })}
                            placeholder="20000"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div>
                          <label className="form-label">Market Value</label>
                          <input
                            type="number"
                            className="form-input"
                            {...register('market_value', { valueAsNumber: true })}
                            placeholder="22000"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>
                    )}

                    <BHPHSettingsSection
                      bhphEligible={watch('bhph_eligible') || false}
                      bhphMinDownType={watch('bhph_min_down_type') || ''}
                      bhphMinDownValue={String(watch('bhph_min_down_value') || '')}
                      bhphPaymentAmount={String(watch('bhph_payment_amount') || '')}
                      bhphPaymentFrequency={watch('bhph_payment_frequency') || ''}
                      bhphTermMonths={String(watch('bhph_term_months') || '')}
                      onChange={(field, value) => {
                        if (typeof value === 'boolean') {
                          setValue(field as any, value, { shouldDirty: true });
                        } else if (
                          [
                            'bhph_min_down_value',
                            'bhph_payment_amount',
                            'bhph_term_months',
                          ].includes(field)
                        ) {
                          setValue(field as any, value ? Number(value) : null, {
                            shouldDirty: true,
                          });
                        } else {
                          setValue(field as any, value, { shouldDirty: true });
                        }
                      }}
                      inputCls="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}

                {/* Description Tab */}
                {activeTab === 'description' && (
                  <div className="space-y-6">
                    {/* Warranty Disclaimer Selector */}
                    <div>
                      <label className="form-label block mb-1">
                        Warranty Disclaimer <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={selectedWarranty}
                        onChange={(e) => setSelectedWarranty(e.target.value)}
                        className={`form-input w-full ${!selectedWarranty ? 'border-amber-400' : 'border-green-500'}`}
                      >
                        <option value="">
                          — Select warranty type (required for AI Generate) —
                        </option>
                        {warrantyOptions.map((o) => (
                          <option key={o.label} value={o.label}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      {warrantyOptions.length === 0 && (
                        <p className="text-xs text-gray-400 mt-1">
                          Configure warranty options in Settings &rarr; Inventory &rarr; Warranty
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedWarranty
                          ? `✓ Selected — AI Generate will include this disclaimer in the output.`
                          : 'Select a warranty type. AI Generate requires this selection.'}
                      </p>
                    </div>

                    <div>
                      <label className="form-label mb-4 block">Vehicle Description</label>
                      <RichTextEditor
                        value={watchedDescription || ''}
                        onChange={(value) => {
                          setValue('description', value, { shouldDirty: true });
                          if (descriptionAiGenerated) setDescriptionUserModified(true);
                        }}
                        placeholder="Create a compelling vehicle description with rich formatting..."
                        onAIGenerate={() => {
                          handleAIDescription();
                        }}
                        isAIGenerating={isGeneratingDescription}
                        minHeight="250px"
                        className="w-full"
                      />
                      {descriptionAiGenerated && descriptionUserModified && (
                        <div className="mt-2 flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                          <span className="text-xs text-amber-700 flex-1">
                            You've edited the AI-generated description. Refactor to have AI
                            incorporate your changes.
                          </span>
                          <button
                            type="button"
                            onClick={handleRefactorDescription}
                            disabled={isRefactoringDescription}
                            className={`flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs rounded-md transition-colors whitespace-nowrap ${
                              isRefactoringDescription
                                ? 'opacity-60 cursor-not-allowed'
                                : 'hover:from-amber-600 hover:to-orange-600'
                            }`}
                          >
                            {isRefactoringDescription ? '⏳ Refactoring…' : '✨ Refactor with AI'}
                          </button>
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="form-label">Features</label>
                        {/*
                          Suggest Features hidden — generic 5-feature output overrides
                          richer CFS-imported features. Handler retained
                          (handleSuggestFeatures) so this can be re-enabled when the
                          suggestion logic preserves existing features.
                        */}
                      </div>
                      <input
                        type="text"
                        className="form-input"
                        value={featuresInput}
                        onChange={(e) => {
                          setFeaturesInput(e.target.value);
                          // Also update form state
                          const features = e.target.value
                            .split(',')
                            .map((f) => f.trim())
                            .filter((f) => f.length > 0);
                          setValue('features', features);
                        }}
                        placeholder="Air Conditioning, Bluetooth, Heated Seats (comma separated)"
                      />
                      <p className="text-sm text-gray-500 mt-1">Separate features with commas</p>
                    </div>
                  </div>
                )}

                {/* Images Tab */}
                {activeTab === 'images' && (
                  <div className="space-y-6">
                    {vehicle?.id ? (
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-4">Vehicle Images</h4>
                        <div>
                          <VehicleImageUpload
                            vehicleId={vehicle.id}
                            images={images}
                            onImagesChange={setImages}
                          />
                        </div>

                        {/* QR Code for mobile upload */}
                        <div className="mt-4">
                          <QRCodeUploader vehicleId={vehicle.id} isNewVehicle={false} />
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <DocumentPlusIcon className="h-6 w-6 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          Save Vehicle First
                        </h3>
                        <p className="text-gray-500 mb-6">
                          Please save the vehicle information first before uploading images.
                        </p>
                        <button
                          type="button"
                          onClick={() => setActiveTab('basic')}
                          className="btn-secondary"
                        >
                          Go to Basic Info
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6">
                  {/* Status Message */}
                  {saveStatus !== 'idle' && (
                    <div
                      className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                        saveStatus === 'saving'
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : saveStatus === 'success'
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-red-50 text-red-700 border border-red-200'
                      }`}
                    >
                      {saveStatus === 'saving' && (
                        <>
                          <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                          {saveMessage}
                        </>
                      )}
                      {saveStatus === 'success' && (
                        <>
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          {saveMessage}
                        </>
                      )}
                      {saveStatus === 'error' && (
                        <>
                          <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                          {saveMessage}
                        </>
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => onClose()}
                    disabled={isSubmitting || loading || saveStatus === 'saving'}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`btn-primary flex items-center ${
                      saveStatus === 'saving' ? 'opacity-75 cursor-not-allowed' : ''
                    }`}
                    disabled={isSubmitting || loading || saveStatus === 'saving'}
                  >
                    {saveStatus === 'saving' ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                        {vehicle ? 'Updating...' : 'Creating...'}
                      </>
                    ) : vehicle ? (
                      'Update Vehicle'
                    ) : (
                      'Add Vehicle'
                    )}
                  </button>
                  {!vehicle && showImageUpload && (
                    <button type="button" className="btn-primary" onClick={() => onClose()}>
                      Done
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleModal;
